import { ChangeDetectionStrategy, Component, computed, ElementRef, inject } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { DevToolsCardService } from './dev-tools-card.service';

const CHEVRON_LEFT = 'M15.75 19.5 8.25 12l7.5-7.5';
const CHEVRON_RIGHT = 'm8.25 4.5 7.5 7.5-7.5 7.5';

@Component({
  selector: 'shared-ui-dev-tools-card',
  templateUrl: './dev-tools-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'devToolsCardService.closeCard()',
  },
  imports: [NgComponentOutlet],
})
export class DevToolsCardComponent {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  protected readonly devToolsCardService = inject(DevToolsCardService);

  // The inputs object is built here and not in the template, so NgComponentOutlet keeps the same
  // reference as long as the providers do not change.
  protected readonly panels = computed(() =>
    this.devToolsCardService
      .providers()
      .map((provider) => ({ provider, inputs: { provider, ...provider.inputs } })),
  );

  protected readonly chevron = computed(() =>
    this.devToolsCardService.isCardOpened() ? CHEVRON_RIGHT : CHEVRON_LEFT,
  );

  // The closed card is only translated away, so without this it would still take tab focus.
  protected readonly inert = computed(() => (this.devToolsCardService.isCardOpened() ? null : ''));

  /**
   * Card and trigger are both children of this host, so anything inside it - a panel button
   * included - leaves the card open. Only a click that lands somewhere else closes it.
   */
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.devToolsCardService.isCardOpened()) return;

    const target = event.target;
    if (target instanceof Node && this.elementRef.nativeElement.contains(target)) return;

    this.devToolsCardService.closeCard();
  }
}
